<template>
  <div class="bg-white border border-gray-200 rounded-lg p-6">
    <div class="flex items-start mb-3">
      <div class="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
        <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      </div>
      <div>
        <h3 class="text-section-header">Two-Factor Authentication</h3>
        <p class="text-caption mt-1">Add an extra layer of security to your account</p>
      </div>
    </div>

    <!-- Status Badge -->
    <div class="mb-4">
      <span v-if="is2FAEnabled"
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        Enabled
      </span>
      <span v-else
            class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 118.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clip-rule="evenodd" />
        </svg>
        Disabled
      </span>
    </div>

    <!-- Description -->
    <div class="mb-6">
      <p class="text-body text-gray-600 mb-3">
        Two-factor authentication adds an extra layer of security to your account by requiring both your password 
        and a verification code from your mobile device when signing in.
      </p>
      
      <div v-if="is2FAEnabled" class="bg-green-50 border border-green-200 rounded-lg p-4">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <div>
            <h4 class="text-sm font-medium text-green-800">2FA is Active</h4>
            <p class="text-sm text-green-700 mt-1">
              Your account is protected with two-factor authentication. You'll need your authenticator app when signing in.
            </p>
          </div>
        </div>
      </div>
      
      <div v-else class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div class="flex items-start">
          <svg class="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          <div>
            <h4 class="text-sm font-medium text-yellow-800">2FA is Disabled</h4>
            <p class="text-sm text-yellow-700 mt-1">
              Your account is not protected by two-factor authentication. We recommend enabling it for better security.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-start">
        <svg class="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
        </svg>
        <div>
          <h4 class="text-sm font-medium text-red-800">Error</h4>
          <p class="text-sm text-red-700 mt-1">{{ error }}</p>
        </div>
      </div>
    </div>

    <!-- Success Message -->
    <div v-if="success" class="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
      <div class="flex items-start">
        <svg class="w-5 h-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <div>
          <h4 class="text-sm font-medium text-green-800">Success</h4>
          <p class="text-sm text-green-700 mt-1">{{ success }}</p>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex flex-col sm:flex-row gap-3">
      <BaseButton
        v-if="!is2FAEnabled"
        variant="primary"
        @click="$emit('enable')"
        :loading="loading"
        :disabled="loading"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        Enable Two-Factor Authentication
      </BaseButton>

      <BaseButton
        v-else
        variant="secondary"
        @click="showDisableDialog = true"
        :disabled="loading"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
        Disable Two-Factor Authentication
      </BaseButton>

      <!-- Backup Codes Button (only when 2FA is enabled) -->
      <BaseButton
        v-if="is2FAEnabled"
        variant="secondary"
        @click="$emit('regenerate-codes')"
        :disabled="loading"
      >
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Regenerate Backup Codes
      </BaseButton>
    </div>

    <!-- Disable 2FA Dialog -->
    <div v-if="showDisableDialog" class="modal-overlay modal-overlay-elevated" @click.self="showDisableDialog = false">
      <div class="modal-content max-w-md" role="dialog" aria-modal="true" aria-labelledby="disable-2fa-title">
        <div class="modal-header">
          <div class="flex items-center">
            <div class="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 class="text-section-header" id="disable-2fa-title">Disable Two-Factor Authentication</h3>
          </div>
          <button @click="showDisableDialog = false" class="modal-close-button" aria-label="Close dialog">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <p class="text-body text-gray-600 mb-4">
            Are you sure you want to disable two-factor authentication? This will make your account less secure.
          </p>
          
          <form @submit.prevent="handleDisable2FA" class="space-y-4">
            <div>
              <label for="disable-password" class="text-label block mb-2">
                Current Password
              </label>
              <input
                id="disable-password"
                v-model="disableForm.password"
                type="password"
                placeholder="Enter your current password"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                :disabled="disableLoading"
                required
              />
            </div>

            <div>
              <label for="disable-code" class="text-label block mb-2">
                Verification Code
              </label>
              <input
                id="disable-code"
                v-model="disableForm.code"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                maxlength="6"
                placeholder="123456"
                class="w-full px-3 py-2 text-center font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                :disabled="disableLoading"
                required
              />
              <p class="mt-1 text-xs text-gray-500">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <BaseButton variant="secondary" @click="showDisableDialog = false" :disabled="disableLoading">
            Cancel
          </BaseButton>
          <BaseButton 
            variant="primary"
            @click="handleDisable2FA"
            :loading="disableLoading"
            :disabled="!disableForm.password || !disableForm.code || disableLoading"
            class="bg-red-600 hover:bg-red-700 focus:ring-red-500"
          >
            Disable 2FA
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import BaseButton from '../BaseButton.vue'
import { useBodyScrollLock } from '../../composables/useBodyScrollLock'

defineProps<{
  is2FAEnabled: boolean
  loading?: boolean
  error?: string
  success?: string
}>()

const emit = defineEmits<{
  (e: 'enable'): void
  (e: 'disable', data: { password: string; code: string }): void
  (e: 'regenerate-codes'): void
}>()

const showDisableDialog = ref(false)
useBodyScrollLock(() => showDisableDialog.value)
const disableLoading = ref(false)

const disableForm = reactive({
  password: '',
  code: ''
})

const handleDisable2FA = async () => {
  if (!disableForm.password || !disableForm.code) return
  
  disableLoading.value = true
  try {
    emit('disable', {
      password: disableForm.password,
      code: disableForm.code
    })
    
    // Reset form
    disableForm.password = ''
    disableForm.code = ''
    showDisableDialog.value = false
  } finally {
    disableLoading.value = false
  }
}
</script>
